import * as React from "react";
import {
  FileArchiveIcon,
  FileAudioIcon,
  FileCodeIcon,
  FileCogIcon,
  FileVideoIcon,
} from "lucide-react";
import { FileIcon } from "@/assets/svgs/fileIcon";
import {
  TEXT_EXTENSIONS,
  CODE_EXTENSIONS,
  ARCHIVE_EXTENSIONS,
  EXECUTABLE_EXTENSIONS,
} from "./constants";

export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(i ? 1 : 0)} ${sizes[i]}`;
}

export function getFileExtension(file: File): string {
  return file.name.split(".").pop()?.toLowerCase() ?? "";
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

export function isPdfFile(file: File): boolean {
  return (
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf")
  );
}

export function isDuplicateFile(
  file: File,
  existingFiles: File[]
): boolean {
  return existingFiles.some(
    (existingFile) =>
      existingFile.name === file.name &&
      existingFile.size === file.size &&
      existingFile.lastModified === file.lastModified
  );
}

export function validateFileType(
  file: File,
  acceptTypes: string[]
): boolean {
  const fileType = file.type;
  const fileExtension = `.${getFileExtension(file)}`;

  return acceptTypes.some(
    (type) =>
      type === fileType ||
      type === fileExtension ||
      (type.includes("/*") && fileType.startsWith(type.replace("/*", "/")))
  );
}

export function getFileIcon(file: File): React.ReactNode {
  const type = file.type;
  const extension = getFileExtension(file);

  if (type.startsWith("video/")) {
    return <FileVideoIcon />;
  }

  if (type.startsWith("audio/")) {
    return <FileAudioIcon />;
  }

  if (type.startsWith("text/") || TEXT_EXTENSIONS.includes(extension)) {
    return <FileIcon />;
  }

  if (CODE_EXTENSIONS.includes(extension)) {
    return <FileCodeIcon className="h-4 w-4" />;
  }

  if (ARCHIVE_EXTENSIONS.includes(extension)) {
    return <FileArchiveIcon className="h-4 w-4" />;
  }

  if (
    EXECUTABLE_EXTENSIONS.includes(extension) ||
    type.startsWith("application/")
  ) {
    return <FileCogIcon className="h-4 w-4" />;
  }

  return <FileIcon />;
}

